/**
 * Bibliothèque centrale des compositions Harmonauria.
 *
 * 👉 Pour ajouter un nouveau morceau, copie un objet existant et adapte les champs.
 * 👉 Tous les morceaux d'un même `intent` doivent partager le même `tuning` (cohérence harmonique).
 * 👉 `arcRole` indique les rôles possibles dans une session de 15 min : "intro", "core", "closure".
 *    Un morceau peut en avoir plusieurs.
 * 👉 `energy` (1-5) : 1 = très doux / 5 = très énergique.
 *    Pour le repos : privilégier 1-2. Pour le recentrage : 2-4.
 *
 * Tuning :
 *   - 432 Hz → paysages de repos
 *   - 444 Hz → paysages de détente et de recentrage
 */

export const MUSICS_LIBRARY = Object.freeze([
  // ============ REST — tuning 432 — ondes Delta ============
  {
    id: "track-01",
    file: "/sounds/audio/1.mp3",
    title: "Regeneration",
    duration: 440,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 285,
    tuning: 432,
    energy: 1,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-02",
    file: "/sounds/audio/2.mp3",
    title: "Emotional Reset",
    duration: 288,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 417,
    tuning: 432,
    energy: 2,
    arcRole: ["intro", "closure"],
  },
  {
    id: "track-03",
    file: "/sounds/audio/3.mp3",
    title: "Stability",
    duration: 466,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 256,
    tuning: 432,
    energy: 1,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-04",
    file: "/sounds/audio/4.mp3",
    title: "Cellular Healing",
    duration: 485,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 285,
    tuning: 432,
    energy: 1,
    arcRole: ["core", "closure"],
  },

    {
    id: "sandman",
    file: "/sounds/audio/sandman.wav",
    title: "Sandman",
    duration: 608,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 285,
    tuning: 432,
    energy: 1,
    arcRole: ["core", "closure"],
  },
  {
    id: "2min-285",
    file: "/sounds/audio/2min-285.wav",
    title: "2min",
    duration: 137,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 285,
    tuning: 432,
    energy: 2,
    arcRole: ["intro"],
  },
  {
    id: "child",
    file: "/sounds/audio/child.wav",
    title: "Child",
    duration: 187,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 256,
    tuning: 432,
    energy: 2,
    arcRole: ["intro", "closure"],
  },
  {
    id: "delta-741-2min",
    file: "/sounds/audio/delta-741.wav",
    title: "Delta 741",
    duration: 144,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 741,
    tuning: 432,
    energy: 1,
    arcRole: ["intro", "closure"],
  },
  {
    id: "asp-delta-741",
    file: "/sounds/audio/asp-delta.wav",
    title: "ASP Delta 741",
    duration: 600,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 741,
    tuning: 432,
    energy: 2,
    arcRole: ["core", "closure"],
  },
  {
    id: "delta-gdiez-417",
    file: "/sounds/audio/delta-gdiez.wav",
    title: "Delta G# 417",
    duration: 373,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 417,
    tuning: 432,
    energy: 1,
    arcRole: ["intro", "core"],
  },
  {
    id: "love-delta-741",
    file: "/sounds/audio/love.wav",
    title: "Love Delta 741",
    duration: 321,
    intent: "rest",
    brainwave: "delta",
    fundamentalHz: 741,
    tuning: 432,
    energy: 2,
    arcRole: ["intro","core", "closure"],
  },

  
  // ============ RELAX — tuning 444 — ondes Theta ============
  {
    id: "track-05",
    file: "/sounds/audio/5.mp3",
    title: "Fear Release",
    duration: 264,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 2,
    arcRole: ["intro", "closure"],
  },
  {
    id: "track-06",
    file: "/sounds/audio/6.mp3",
    title: "Guilt Cleansing",
    duration: 640,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 3,
    arcRole: ["core"],
  },
  {
    id: "track-07",
    file: "/sounds/audio/7.mp3",
    title: "Inner Safety",
    duration: 384,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 2,
    arcRole: ["core"],
  },
  {
    id: "track-08",
    file: "/sounds/audio/8.mp3",
    title: "Liberation & Grounding",
    duration: 316,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 2,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-09",
    file: "/sounds/audio/9.mp3",
    title: "Deep Pain Relief",
    duration: 497,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 174,
    tuning: 444,
    energy: 1,
    arcRole: ["core", "closure"],
  },
  {
    id: "track-10",
    file: "/sounds/audio/10.mp3",
    title: "Body Calm Restoration",
    duration: 434,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 174,
    tuning: 444,
    energy: 3,
    arcRole: ["core", "closure"],
  },
  {
    id: "track-11",
    file: "/sounds/audio/11.mp3",
    title: "Emotional Reset",
    duration: 304,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 417,
    tuning: 444,
    energy: 2,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-12",
    file: "/sounds/audio/12.mp3",
    title: "Change & Renewal",
    duration: 329,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 417,
    tuning: 444,
    energy: 1,
    arcRole: ["intro", "core"],
  },
  {
    id: "tree",
    file: "/sounds/audio/396-tree.wav",
    title: "tree",
    duration: 864,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 2,
    arcRole: ["intro", "core", "closure"],
  },
{
  id: "mpe",
  file: "/sounds/audio/mpe.mp3",
  title: "MPE",
  duration: 317,
  intent: "relax",
  brainwave: "theta",
  fundamentalHz: 444,
  tuning: 444,
  energy: 2,
  arcRole: ["intro", "core", "closure"],
},
  {
    id: "333-theta-396",
    file: "/sounds/audio/333.wav",
    title: "333 Theta 396",
    duration: 145,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 396,
    tuning: 444,
    energy: 3,
  arcRole: ["intro", "core", "closure"],
  },
  {
    id: "delta-174hz-ms-theta",
    file: "/sounds/audio/delta-174.wav",
    title: "Delta 174Hz MS Theta",
    duration: 350,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 174,
    tuning: 444,
    energy: 2,
    arcRole: ["intro", "core", "closure"],
  },
  {
    id: "galaxy-13610-theta",
    file: "/sounds/audio/galaxy.wav",
    title: "Galaxy Theta 136.10",
    duration: 326,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 136.10,
    tuning: 444,
    energy: 3,
    arcRole: ["intro", "core", "closure"],
  },
    {
    id: "queen-theta-174",
    file: "/sounds/audio/queen.wav",
    title: "Queen Theta 174",
    duration: 192,
    intent: "relax",
    brainwave: "theta",
    fundamentalHz: 174,
    tuning: 444,
    energy: 3,
    arcRole: ["intro", "core", "closure"],
  },


  // ============ RECENTER — tuning 444 — ondes Alpha/Beta ============
  {
    id: "track-13",
    file: "/sounds/audio/13.mp3",
    title: "Deep Focus Activation",
    duration: 104,
    intent: "recenter",
    brainwave: "beta",
    fundamentalHz: 256,
    tuning: 444,
    energy: 3,
    arcRole: ["intro"],
  },
  {
    id: "track-14",
    file: "/sounds/audio/14.mp3",
    title: "Mental Clarity Reset",
    duration: 303,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 256,
    tuning: 444,
    energy: 3,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-15",
    file: "/sounds/audio/15.mp3",
    title: "Clear Mind",
    duration: 487,
    intent: "recenter",
    brainwave: "beta",
    fundamentalHz: 417,
    tuning: 444,
    energy: 2,
    arcRole: ["core"],
  },
  {
    id: "track-16",
    file: "/sounds/audio/16.mp3",
    title: "Concentration Stream",
    duration: 367,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 256,
    tuning: 444,
    energy: 4,
    arcRole: ["core", "closure"],
  },
  {
    id: "track-17",
    file: "/sounds/audio/17.wav",
    title: "beta 147",
    duration: 558,
    intent: "recenter",
    brainwave: "beta",
    fundamentalHz: 147,
    tuning: 444,
    energy: 4,
    arcRole: ["core"],
  },
  {
    id: "track-18",
    file: "/sounds/audio/18.wav",
    title: "alpha 136.10 528",
    duration: 302,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 136.10,
    tuning: 444,
    energy: 3,
    arcRole: ["intro", "core"],
  },

  {
    id: "track-19",
    file: "/sounds/audio/1-theta.wav",
    title: "Theta Drift",
    duration: 442,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 3,
    arcRole: ["core"],
  },
  {
    id: "track-20",
    file: "/sounds/audio/2-3-piano.wav",
    title: "Piano Reverie",
    duration: 275,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 4,
    arcRole: ["intro", "closure"],
  },
  {
    id: "track-21",
    file: "/sounds/audio/3-new-last.wav",
    title: "Final Light",
    duration: 324,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 3,
    arcRole: ["core", "closure"],
  },
  {
    id: "track-22",
    file: "/sounds/audio/4-malheur.wav",
    title: "Sorrow Release",
    duration: 265,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 4,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-23",
    file: "/sounds/audio/5-electric.wav",
    title: "Electric Flow",
    duration: 318,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 3,
    arcRole: ["intro", "core"],
  },
  {
    id: "track-24",
    file: "/sounds/audio/6-alpha-741.wav",
    title: "Alpha 741",
    duration: 328,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 4,
    arcRole: ["core"],
  },
    {
    id: "floor",
    file: "/sounds/audio/floor.mp3",
    title: "floor",
    duration: 309,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 174,
    tuning: 444,
    energy: 4,
    arcRole: ["intro", "core"],
  },
    {
    id: "lofifi-5-alpha-432",
    file: "/sounds/audio/lofifi.wav",
    title: "Lofifi Alpha",
    duration: 405,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 432,
    tuning: 444,
    energy: 4,
    arcRole: ["core", "closure"],
  },
    {
    id: "dsampler-396hz-alpha",
    file: "/sounds/audio/dsampler.wav",
    title: "DSampler 396Hz Alpha",
    duration: 617,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 396,
    tuning: 444,
    energy: 3,
    arcRole: ["intro","core", "closure"],
  },
    {
    id: "ai-generation",
    file: "/sounds/audio/aigeneration.wav",
    title: "AI Generation",
    duration: 325,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 174,
    tuning: 444,
    energy: 3,
    arcRole: ["intro","core", "closure"],
  },
  {
    id: "417-theta-roads-alpha-444",
    file: "/sounds/audio/417-theta.wav",
    title: "417 Theta Roads",
    duration: 300,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 444,
    tuning: 444,
    energy: 2,
    arcRole: ["intro","core", "closure"],
  },
    {
    id: "keepgoing-741-alpha",
    file: "/sounds/audio/keepgoing.wav",
    title: "Keep Going 741",
    duration: 610,
    intent: "recenter",
    brainwave: "alpha",
    fundamentalHz: 741,
    tuning: 444,
    energy: 3,
    arcRole: ["intro","core", "closure"],
  },


]);

// Helpers
export function getTracksByIntent(intent) {
  return MUSICS_LIBRARY.filter((t) => t.intent === intent);
}

export function getTrackById(id) {
  return MUSICS_LIBRARY.find((t) => t.id === id) || null;
}